/**
 * This service function makes satisfying the
 * [@typescript-eslint/strict-boolean-expressions](https://typescript-eslint.io/rules/strict-boolean-expressions/)
 * lint rule a bit easier by preventing you from having to type out the checks each time.
 *
 * See `tsServices.ts` for other service functions that aid with this rule.
 */
export function isNullOrUndefined(arg: unknown): arg is null | undefined {
  return arg === null || arg === undefined
}
/**
 * This service function makes satisfying the
 * [@typescript-eslint/strict-boolean-expressions](https://typescript-eslint.io/rules/strict-boolean-expressions/)
 * lint rule a bit easier by preventing you from having to type out the checks each time.
 *
 * See `tsServices.ts` for other service functions that aid with this rule.
 */
export function isNotNullOrUndefined<T>(arg: T): arg is NonNullable<T> {
  return arg !== undefined && arg !== null
}
/**
 * This service function makes satisfying the
 * [@typescript-eslint/strict-boolean-expressions](https://typescript-eslint.io/rules/strict-boolean-expressions/)
 * lint rule a bit easier by preventing you from having to type out the checks each time.
 *
 * Do not use the NOT (!) operator with this function.
 * Doing so results in the argument being evaluated to have a type of `never`
 * which can be unhelpful or problematic.
 * Instead, use `isNotNullOrUndefinedOrEmptyString`.
 *
 * See `tsServices.ts` for other service functions that aid with this rule.
 */
export function isNullOrUndefinedOrEmptyString(
  arg: string | null | undefined
): arg is string | null | undefined {
  return arg === undefined || arg === null || arg === ''
}
/**
 * This service function makes satisfying the
 * [@typescript-eslint/strict-boolean-expressions](https://typescript-eslint.io/rules/strict-boolean-expressions/)
 * lint rule a bit easier by preventing you from having to type out the checks each time.
 *
 * Do not use the NOT (!) operator with this function.
 * Doing so results in the argument being evaluated to a type of `null | undefined`
 * even though it should be `null | undefined | string`.
 * Instead, use `isNullOrUndefinedOrEmptyString`.
 *
 * See `tsServices.ts` for other service functions that aid with this rule.
 */
export function isNotNullOrUndefinedOrEmptyString(arg: string | null | undefined): arg is string {
  return arg !== undefined && arg !== null && arg !== ''
}
/**
 * This service function makes satisfying the
 * [@typescript-eslint/strict-boolean-expressions](https://typescript-eslint.io/rules/strict-boolean-expressions/)
 * lint rule a bit easier by preventing you from having to type out the checks each time.
 *
 * Do not use the NOT (!) operator with this function.
 * Doing so results in the argument being evaluated to have a type of `never`
 * which can be unhelpful or problematic.
 * Instead, use `isNotNullOrUndefinedOrZeroOrNaN`.
 *
 * See `tsServices.ts` for other service functions that aid with this rule.
 */
export function isNullOrUndefinedOrZeroOrNaN(
  arg: number | null | undefined
): arg is number | null | undefined {
  return arg === undefined || arg === null || arg === 0 || Number.isNaN(arg)
}
/**
 * This service function makes satisfying the
 * [@typescript-eslint/strict-boolean-expressions](https://typescript-eslint.io/rules/strict-boolean-expressions/)
 * lint rule a bit easier by preventing you from having to type out the checks each time.
 *
 * Do not use the NOT (!) operator with this function.
 * Doing so results in the argument being evaluated to a type of `null | undefined`
 * even though it should be `null | undefined | number`.
 * Instead, use `isNullOrUndefinedOrZeroOrNaN`.
 *
 * See `tsServices.ts` for other service functions that aid with this rule.
 */
export function isNotNullOrUndefinedOrZeroOrNaN(arg: number | null | undefined): arg is number {
  return arg !== undefined && arg !== null && arg !== 0 && !Number.isNaN(arg)
}

export function isNumber(arg: unknown): arg is number {
  return typeof arg === 'number'
}

export function isString(arg: unknown): arg is string {
  return typeof arg === 'string'
}

export function isBoolean(arg: unknown): arg is boolean {
  return typeof arg === 'boolean'
}

export function isArray<T = unknown>(arg: unknown): arg is T[] {
  return typeof arg === 'object' && Array.isArray(arg)
}

export function isNull(arg: unknown): arg is null {
  return typeof arg === 'object' && arg === null
}

export function isObject<T = Record<string, any>>(arg: unknown): arg is T {
  return typeof arg === 'object' && !Array.isArray(arg) && arg !== null
}

export function isUndefined(arg: unknown): arg is void {
  return typeof arg === 'undefined'
}

export const determineExactType = (
  variable: any
):
  | 'number'
  | 'string'
  | 'boolean'
  | 'array'
  | 'object'
  | 'null'
  | 'undefined'
  | 'function'
  | 'unknown' => {
  // Check the type of the variable
  switch (typeof variable) {
    case 'number':
      return 'number'
    case 'string':
      return 'string'
    case 'boolean':
      return 'boolean'
    case 'object':
      if (Array.isArray(variable)) {
        return 'array'
      } else if (variable === null) {
        return 'null'
      } else {
        return 'object'
      }
    case 'undefined':
      return 'undefined'
    case 'function':
      return 'function'
    default:
      return 'unknown'
  }
}
